import React, { FunctionComponent } from 'react';

import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import AppState from '@webapp/store/state';

import Box from '@components/layout/box';
import Grid from '@components/basics/grid';
import Flex from '@components/layout/flex';
import Hint from '@components/basics/hint';
import TitleWithHelper from '@components/title-w-helper';
import AnalyticsContainer from '@components/layout/analytics-container';

import AnalyticsMenu from '@webapp/controllers/menus/analytics-menu';

import MapBox from './map-box';
import DspsBox from './dsps-box';
import FanTypeBox from './fan-type-box';
import TopSongsBox from './top-songs-box';
import AgeRangeBox from './age-range-box';
import TopLocations from './top-locations';
import { useSelector } from 'react-redux';
import FanGenderBox from './fan-gender-box';
import ActivityGraphBox from './activity-graph-box';
import AnalyticsGraphLegend from './activity-graph-legend';
import ArtificialStreamsBox from './artificial-streams-box';
import Pill from '@components/basics/pill';

interface NewAnalyticsPageProps {
  className?: string;
  isMobile: boolean;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

const NewAnalyticsPage: FunctionComponent<NewAnalyticsPageProps> = ({
  className,
  isMobile,
}) => {
  const [IsExampleData, days, range] = useSelector((state: AppState) => {
    return [
      state.artistAnalytics.isExample as boolean,
      state.artistAnalytics.filters?.days as string,
      state.artistAnalytics.filters?.range as string,
    ];
  });
  // TODO: DEVTEAM-405 - This need to be integrated with the backend.
  const artificialStreams = false;

  return (
    <AnalyticsContainer className={className}>
      {(IsExampleData || artificialStreams) && (
        <StyledHint
          error={artificialStreams}
          customTitle={
            <Translate
              id={
                IsExampleData
                  ? 'ANALYTICS.HINT.EXAMPLE_DATA.TITLE'
                  : 'ANALYTICS.HINT.ARTIFICIAL_STREAMS.TITLE'
              }
            />
          }
          helperText={
            <Translate
              id={
                IsExampleData
                  ? 'ANALYTICS.ENGAGEMENT.TITLE.HELPER'
                  : 'ANALYTICS.ENGAGEMENT.ARTIFICIAL.TITLE.HELPER'
              }
            />
          }
          helperTextId="analytics-hint"
          noHelperText={!!IsExampleData}
        >
          <Translate
            id={
              IsExampleData
                ? 'ANALYTICS.HINT.EXAMPLE_DATA.TEXT'
                : 'ANALYTICS.HINT.ARTIFICIAL_STREAMS.TEXT'
            }
          />
        </StyledHint>
      )}
      <TitleWrapper>
        <TitleWithHelper
          id="analyticsEngagement"
          title={<Translate id="ANALYTICS.ENGAGEMENT.TITLE" />}
          helperText={<Translate id="ANALYTICS.ENGAGEMENT.TITLE.HELPER" />}
          // TODO: DEVTEAM-405 - The subtitle must came from the selected dates on the filter, the default option should be the one below.
          subtitle={
            range === 'allTime' ? (
              <Translate id="ANALYTICS.TIMEFRAME.ALL_TIME" />
            ) : (
              <Translate
                id="ANALYTICS.TIMEFRAME.LAST_30_DAYS"
                data={{ days: days }}
              />
            )
          }
        />
        <AnalyticsMenu />
      </TitleWrapper>
      <Wrapper className="engagement">
        <ActivityGraphBox className="activity-graph-box" />
        {isMobile && (
          <Box className="streams-box" noBorderBottom noBorderTop>
            <AnalyticsGraphLegend />
          </Box>
        )}
        {isMobile && (
          <ArtificialStreamsBox
            className="artificial-streams-box"
            isComingSoon
          />
        )}
        <TopSongsBox className="top-songs-box" />
        <Grid className="artificial-streams-wrapper">
          {!isMobile && (
            <ArtificialStreamsBox
              className="artificial-streams-box"
              isComingSoon
            />
          )}
          <DspsBox className="dsps-box" />
        </Grid>
      </Wrapper>
      <TitleWrapper>
        <TitleWithHelper
          id="analyticsFans"
          title={<Translate id="ANALYTICS.FANS.TITLE" />}
          titleDecoration={
            <Pill bordered>
              <Translate id="COMMON.BETA" />
            </Pill>
          }
          // helperText={<Translate id="ANALYTICS.FANS.TITLE.HELPER" />}
          // TODO: DEVTEAM-405 - The subtitle must came from the selected dates on the filter, the default option should be the one below.
          subtitle={
            range === 'allTime' ? (
              <Translate id="ANALYTICS.TIMEFRAME.ALL_TIME" />
            ) : (
              <Translate
                id="ANALYTICS.TIMEFRAME.LAST_30_DAYS"
                data={{ days: days }}
              />
            )
          }
        />
      </TitleWrapper>
      <Wrapper className="fans">
        <MapBox className="map-box" />
        {isMobile && (
          <Box className="top-locations-box" noBorderTop noBorderBottom>
            <TopLocations />
          </Box>
        )}
        <AgeRangeBox className="age-range-box" />
        <Grid className="fan-wrapper">
          <FanTypeBox className="fan-type-box" isComingSoon />
          <FanGenderBox className="fan-gender-box" />
        </Grid>
      </Wrapper>
    </AnalyticsContainer>
  );
};

export default connect(mapStateToProps)(NewAnalyticsPage);

const Wrapper = styled.div`
  display: grid;
  grid-gap: 3px;

  .recharts-tooltip-wrapper {
    outline: none !important;
  }

  @media ${({ theme }) => theme.device.tablet} {
    grid-template-columns: repeat(18, minmax(0, 1fr));
  }

  &.engagement {
    margin-bottom: 40px;
    @media ${({ theme }) => theme.device.tablet} {
      margin-bottom: 80px;
    }

    .activity-graph-box {
      grid-row: 1;
      @media ${({ theme }) => theme.device.tablet} {
        grid-column: span 18;
      }
    }
    .streams-box {
      grid-row: 2;
    }
    .top-songs-box {
      grid-row: 4;
      @media ${({ theme }) => theme.device.tablet} {
        grid-row: 2;
        grid-column: 1 / 10;
      }
    }
    .artificial-streams-box {
      grid-row: 3;
      @media ${({ theme }) => theme.device.tablet} {
        display: flex;
        align-items: center;
        justify-content: center;
        grid-row: -1;
      }
    }
    .artificial-streams-wrapper {
      grid-row: 5;
      @media ${({ theme }) => theme.device.tablet} {
        grid-row: 2;
        grid-column: 10 / 19;
      }
    }
  }

  &.fans {
    .map-box {
      grid-row: 1;
      @media ${({ theme }) => theme.device.tablet} {
        grid-column: span 18;
      }
    }
    .top-locations-box {
      grid-row: 2;
    }
    .age-range-box {
      grid-row: 4;
      @media ${({ theme }) => theme.device.tablet} {
        grid-row: 2;
        grid-column: 1 / 10;
      }
    }
    .fan-wrapper {
      grid-row: 3;
      @media ${({ theme }) => theme.device.tablet} {
        grid-row: 2;
        grid-column: 10 / 19;

        .fan-type-box {
          grid-row: 1;
        }
        .fan-gender-box {
          grid-row: 2 / 4;
        }
      }
    }
  }
`;

const StyledHint = styled(Hint)`
  border-radius: 10px;
  margin-bottom: 40px;
  @media ${({ theme }) => theme.device.tablet} {
    padding: 30px;
  }
`;

const TitleWrapper = styled(Flex).attrs(() => ({
  justifyContent: 'space-between',
  alignItems: 'center',
}))`
  margin-bottom: 30px;
  position: relative;
`;
